import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { getInquiries, updateInquiry } from '../../api/inquiries';
import InquiryDetailModal from './InquiryDetailModal';
import InquiryKanban from './InquiryKanban';
import AdminButton from '../../components/admin/AdminButton';

// Helper for date formatting
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
};

const InquiryList = () => {
    const { user } = useAuth();
    const [proposals, setProposals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'kanban'

    // Filters & Pagination State
    const [filters, setFilters] = useState({
        type: '',
        status: '',
        search: '',
        page: 1,
        limit: 40,
        sortField: 'createdAt',
        sortOrder: 'DESC'
    });

    // Search Input State (separate to avoid api call on every keystroke)
    const [searchInput, setSearchInput] = useState('');

    const [total, setTotal] = useState(0);
    const [selectedInquiryId, setSelectedInquiryId] = useState(null);

    const fetchProposals = async () => {
        setLoading(true);
        try {
            const data = await getInquiries(filters);
            setProposals(data.data);
            setTotal(data.total);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProposals();
    }, [filters, user.token]);

    // Handlers
    const handleSort = (field) => {
        setFilters(prev => ({
            ...prev,
            sortField: field,
            sortOrder: prev.sortField === field && prev.sortOrder === 'DESC' ? 'ASC' : 'DESC'
        }));
    };

    const handleSearch = () => {
        setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            if (!window.confirm('상태를 변경하시겠습니까?')) return;
            await updateInquiry(id, { status: newStatus });
            setProposals(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p));
        } catch (error) {
            console.error(error);
            alert('상태 변경 실패');
        }
    };

    const getSortIcon = (field) => {
        if (filters.sortField !== field) return '↕';
        return filters.sortOrder === 'ASC' ? '▲' : '▼';
    };

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
                <h2 className="text-2xl font-extrabold text-gray-800 m-0 tracking-tight">고객 요청 관리</h2>

                <div className="flex flex-col xl:flex-row items-start xl:items-center gap-4 w-full md:w-auto">
                    {/* Search Box */}
                    <div className="flex w-full xl:w-auto gap-2">
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="내용, 회사, 고객명 검색"
                            className="flex-1 xl:w-64 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none shadow-sm text-sm"
                        />
                        <AdminButton variant="primary" onClick={handleSearch} className="!px-5 !bg-gray-800 hover:!bg-gray-900 border-transparent text-white whitespace-nowrap">검색</AdminButton>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        {/* Status Filters */}
                        <div className="flex items-center gap-1 bg-white rounded-xl shadow-sm border border-gray-200 p-1 flex-wrap">
                            {['', 'UNREAD', 'READ', 'RESPONDED', 'DONE', 'REJECTED'].map(status => (
                                <button
                                    key={status}
                                    className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${filters.status === status ? (status === 'UNREAD' ? 'bg-red-100 text-red-600' : 'bg-primary-500 text-white') : 'text-gray-500 hover:bg-gray-100'}`}
                                    onClick={() => setFilters({ ...filters, status, page: 1 })}
                                >
                                    {status === '' ? '모든 상태' :
                                        status === 'UNREAD' ? '읽지 않음' :
                                        status === 'READ' ? '읽음' :
                                        status === 'RESPONDED' ? '답변 완료' :
                                        status === 'DONE' ? '종료' : '거절됨'}
                                </button>
                            ))}
                        </div>

                        {/* View Mode Toggle */}
                        <div className="flex items-center gap-1 bg-white rounded-xl shadow-sm border border-gray-200 p-1 ml-auto">
                            <button
                                className={`px-4 py-1.5 text-sm font-bold rounded-lg transition-colors flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                                onClick={() => setViewMode('list')}
                            >
                                📋 목록
                            </button>
                            <button
                                className={`px-4 py-1.5 text-sm font-bold rounded-lg transition-colors flex items-center gap-1.5 ${viewMode === 'kanban' ? 'bg-gray-800 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                                onClick={() => setViewMode('kanban')}
                            >
                                🗂️ 칸반
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-between items-center mb-4 text-sm text-gray-600">
                <div>총 <strong className="text-gray-900 text-base">{total}</strong>건의 인바운드 문의</div>
                <select
                    value={filters.limit}
                    onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value), page: 1 })}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-primary-500 bg-white shadow-sm font-semibold"
                >
                    <option value={40}>40개 보기</option>
                    <option value={100}>100개 보기</option>
                </select>
            </div>

            <div className="relative min-h-[500px]">
                {loading && (
                    <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex justify-center items-center z-10 rounded-2xl">
                        <div className="text-lg font-bold text-gray-400 animate-pulse">데이터 로딩 중...</div>
                    </div>
                )}

                {viewMode === 'kanban' ? (
                    <InquiryKanban 
                        proposals={proposals} 
                        handleStatusChange={handleStatusChange} 
                        setSelectedInquiryId={setSelectedInquiryId} 
                    />
                ) : (
                    <>
                        {/* Desktop Table View (Hidden on Mobile) */}
                        <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                            <table className="w-full text-left text-sm whitespace-nowrap">
                                <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="px-5 py-4 font-bold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('id')}>No. {getSortIcon('id')}</th>
                                        <th className="px-5 py-4 font-bold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('type')}>유형 {getSortIcon('type')}</th>
                                        <th className="px-5 py-4 font-bold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('companyName')}>회사명 {getSortIcon('companyName')}</th>
                                        <th className="px-5 py-4 font-bold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('customerName')}>고객명 {getSortIcon('customerName')}</th>
                                        <th className="px-5 py-4 font-bold w-1/3">요청 내용</th>
                                        <th className="px-5 py-4 font-bold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('createdAt')}>등록일 {getSortIcon('createdAt')}</th>
                                        <th className="px-5 py-4 font-bold text-center">상태 변경</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-gray-700 bg-white">
                                    {proposals.map((proposal) => (
                                        <tr key={proposal.id} onClick={() => setSelectedInquiryId(proposal.id)} className="hover:bg-primary-50 cursor-pointer transition-colors group">
                                            <td className="px-5 py-3.5 font-mono text-gray-400 text-xs">{proposal.id}</td>
                                            <td className="px-5 py-3.5">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${proposal.type === 'ESTIMATE' ? 'bg-green-100 text-green-700' : proposal.type === 'DEMO' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                                    {proposal.type}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5 font-extrabold text-gray-900">{proposal.companyName || '-'}</td>
                                            <td className="px-5 py-3.5 font-semibold text-gray-700">{proposal.customerName}</td>
                                            <td className="px-5 py-3.5 truncate max-w-[250px] text-gray-500 group-hover:text-gray-800">{proposal.message || '-'}</td>
                                            <td className="px-5 py-3.5 text-xs text-gray-400">{formatDate(proposal.createdAt).split(' ')[0]}</td>
                                            <td className="px-5 py-3.5 text-center" onClick={(e) => e.stopPropagation()}>
                                                <select
                                                    value={proposal.status}
                                                    onChange={(e) => handleStatusChange(proposal.id, e.target.value)}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border outline-none cursor-pointer hover:shadow-md transition-shadow ${
                                                        proposal.status === 'UNREAD' ? 'bg-red-50 text-red-600 border-red-200' :
                                                        proposal.status === 'RESPONDED' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                                                        proposal.status === 'DONE' ? 'bg-gray-100 text-gray-500 border-gray-200' :
                                                        'bg-white border-gray-300 text-gray-700'
                                                    }`}
                                                >
                                                    <option value="UNREAD">읽지 않음</option>
                                                    <option value="READ">확인함</option>
                                                    <option value="RESPONDED">응답 완료</option>
                                                    <option value="DONE">계약/종료</option>
                                                    <option value="REJECTED">거절됨</option>
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                    {!loading && proposals.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-16 text-center text-gray-400 font-semibold bg-gray-50">인바운드 파이프라인이 비어 있습니다.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile CRM Card View */}
                        <div className="md:hidden grid gap-4">
                            {proposals.map((proposal) => (
                                <div key={proposal.id} onClick={() => setSelectedInquiryId(proposal.id)} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 relative active:scale-[0.98] transition-transform flex flex-col">
                                    <div className="flex justify-between items-center mb-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-mono text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">#{proposal.id}</span>
                                            <span className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold ${proposal.type === 'ESTIMATE' ? 'bg-green-100 text-green-700' : proposal.type === 'DEMO' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                                {proposal.type}
                                            </span>
                                        </div>
                                        <div className="text-[11px] font-medium text-gray-400">{formatDate(proposal.createdAt).split(' ')[0]}</div>
                                    </div>
                                    <div className="mb-2">
                                        <h3 className="font-extrabold text-lg text-gray-900 leading-tight">{proposal.companyName || '개인고객/회사미상'}</h3>
                                        <div className="text-sm font-bold text-gray-500 mt-0.5">{proposal.customerName} 고객님</div>
                                    </div>
                                    <p className="text-sm text-gray-600 line-clamp-2 mb-5 leading-relaxed bg-gray-50 p-3 rounded-xl border border-gray-100">
                                        {proposal.message || '요청 내용이 없습니다.'}
                                    </p>
                                    <div className="flex justify-between items-center mt-auto pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                                        <span className="text-xs font-bold text-gray-400">상태 변경</span>
                                        <select
                                            value={proposal.status}
                                            onChange={(e) => handleStatusChange(proposal.id, e.target.value)}
                                            className={`px-4 py-2 w-44 rounded-xl text-xs font-extrabold border outline-none shadow-sm ${
                                                proposal.status === 'UNREAD' ? 'bg-red-50 text-red-600 border-red-200' :
                                                proposal.status === 'RESPONDED' ? 'bg-blue-50 text-blue-600 border-blue-200' :
                                                proposal.status === 'DONE' ? 'bg-gray-100 text-gray-500 border-gray-200' :
                                                'bg-white border-gray-300 text-gray-700'
                                            }`}
                                        >
                                            <option value="UNREAD">🔴 읽지 않음 (위급)</option>
                                            <option value="READ">🔘 내용 확인 (보류)</option>
                                            <option value="RESPONDED">🔵 응답 완료 (진행)</option>
                                            <option value="DONE">⚫ 종료 (완료)</option>
                                            <option value="REJECTED">⛔ 거절</option>
                                        </select>
                                    </div>
                                </div>
                            ))}
                            {!loading && proposals.length === 0 && (
                                <div className="p-10 text-center text-gray-400 bg-white rounded-2xl border border-dashed border-gray-300 font-bold">표시할 데이터가 없습니다.</div>
                            )}
                        </div>
                    </>
                )}

                {/* Pagination Controls */}
                <div className="mt-8 flex justify-center gap-2">
                    <button disabled={filters.page === 1} onClick={() => setFilters({ ...filters, page: 1 })} className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 font-bold disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 transition-colors">«</button>
                    <button disabled={filters.page === 1} onClick={() => setFilters({ ...filters, page: filters.page - 1 })} className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 font-bold disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 transition-colors">‹</button>

                    {Array.from({ length: Math.min(5, Math.ceil(total / filters.limit) || 1) }, (_, i) => {
                        const startPage = Math.max(1, filters.page - 2);
                        const p = startPage + i;
                        if (p > Math.ceil(total / filters.limit)) return null;
                        return (
                            <button
                                key={p}
                                className={`w-11 h-11 flex items-center justify-center rounded-xl font-extrabold text-sm transition-all shadow-sm ${filters.page === p ? 'bg-primary-500 text-white shadow-primary-500/30' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 relative top-0 hover:-top-0.5'}`}
                                onClick={() => setFilters({ ...filters, page: p })}
                            >
                                {p}
                            </button>
                        );
                    })}

                    <button disabled={filters.page >= Math.ceil(total / filters.limit)} onClick={() => setFilters({ ...filters, page: filters.page + 1 })} className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 font-bold disabled:opacity-50 disabled:cursor-not-allowed text-gray-500 transition-colors">›</button>
                </div>
            </div>

            {selectedInquiryId && (
                <InquiryDetailModal
                    inquiryId={selectedInquiryId}
                    onClose={() => setSelectedInquiryId(null)}
                />
            )}
        </AdminLayout>
    );
};

export default InquiryList;
